import { useState, useEffect, useCallback } from "react";
import { disruptionAPI } from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import { toast } from "sonner";
import {
  Presentation, ChevronLeft, ChevronRight, X, Users, MessageSquare,
  Skull, Rocket, Maximize2, Minimize2,
} from "lucide-react";

const PresentationMode = ({ boardId, session, isOwner, onClose }) => {
  const [allResponses, setAllResponses] = useState([]);
  const [selectedPairIdx, setSelectedPairIdx] = useState(0);
  const [currentQuestionIdx, setCurrentQuestionIdx] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const fetchAllResponses = useCallback(async () => {
    try {
      const res = await disruptionAPI.getResponses(boardId);
      setAllResponses(res.data.responses || []);
    } catch { /* silent */ }
  }, [boardId]);

  useEffect(() => { fetchAllResponses(); }, [fetchAllResponses]);

  const pairs = session?.pairs || [];
  const currentPair = pairs[selectedPairIdx];
  const phase = session?.current_phase || "phase1";
  const questions = session?.questions?.[phase];
  const isPhase1 = phase === "phase1";

  // Get all questions flat
  const allQuestions = questions?.categories?.flatMap((c) =>
    c.questions.map((q) => ({ ...q, category: c.name }))
  ) || [];

  // Get questions assigned to this pair
  const pairQuestionIds = new Set(currentPair?.[`${phase}_questions`] || []);
  const pairQuestions = pairQuestionIds.size > 0
    ? allQuestions.filter((q) => pairQuestionIds.has(q.id))
    : allQuestions;

  const currentQuestion = pairQuestions[currentQuestionIdx];

  // Get responses for current question from pair members
  const pairMemberIds = new Set((currentPair?.members || []).map((m) => m.user_id));
  const questionResponses = allResponses.filter(
    (r) => r.question_id === currentQuestion?.id && pairMemberIds.has(r.user_id)
  );

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  };

  const nextQuestion = () => {
    if (currentQuestionIdx < pairQuestions.length - 1) {
      setCurrentQuestionIdx(currentQuestionIdx + 1);
    }
  };

  const prevQuestion = () => {
    if (currentQuestionIdx > 0) {
      setCurrentQuestionIdx(currentQuestionIdx - 1);
    }
  };

  const selectPair = (idx) => {
    setSelectedPairIdx(idx);
    setCurrentQuestionIdx(0);
  };

  const PersonaIcon = isPhase1 ? Skull : Rocket;
  const personaColor = isPhase1 ? "text-red-500" : "text-indigo-500";
  const personaBg = isPhase1 ? "bg-red-50" : "bg-indigo-50";

  return (
    <div className="fixed inset-0 z-50 bg-slate-900 flex flex-col" data-testid="presentation-mode">
      {/* Top Bar */}
      <div className="bg-slate-800 border-b border-slate-700 px-6 py-3 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <Presentation className="w-5 h-5 text-indigo-400" />
          <span className="font-heading font-semibold text-white text-sm">Presentation Mode</span>
          <Badge className={`text-[10px] ${isPhase1 ? "bg-red-500/20 text-red-300 border-red-500/30" : "bg-indigo-500/20 text-indigo-300 border-indigo-500/30"}`}>
            {isPhase1 ? "Phase 1: The Attack" : "Phase 2: Company 2.0"}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={toggleFullscreen} className="text-slate-400 hover:text-white h-8 w-8 p-0">
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </Button>
          <Button variant="ghost" size="sm" data-testid="close-presentation-btn" onClick={onClose} className="text-slate-400 hover:text-white h-8 w-8 p-0">
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Pair Selector Sidebar */}
        <aside className="w-56 bg-slate-800 border-r border-slate-700 flex flex-col shrink-0">
          <div className="px-4 py-3 border-b border-slate-700">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Select Pair</span>
          </div>
          <ScrollArea className="flex-1">
            <div className="py-2">
              {pairs.map((pair, idx) => {
                const pqIds = new Set(pair[`${phase}_questions`] || []);
                const answeredCount = allResponses.filter(
                  (r) => pqIds.has(r.question_id) && pair.members?.some((m) => m.user_id === r.user_id) && r.response_text
                ).length;
                return (
                  <button
                    key={pair.id}
                    data-testid={`select-pair-${idx}`}
                    onClick={() => selectPair(idx)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                      selectedPairIdx === idx
                        ? "bg-indigo-600/20 border-r-2 border-indigo-400"
                        : "hover:bg-slate-700/50"
                    }`}
                  >
                    <div className="flex -space-x-1.5">
                      {pair.members?.map((m) => (
                        <div key={m.user_id} className="w-7 h-7 rounded-full bg-indigo-500 flex items-center justify-center text-[10px] font-bold text-white ring-2 ring-slate-800">
                          {(m.name || "?")[0].toUpperCase()}
                        </div>
                      ))}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-slate-300 truncate">
                        {pair.members?.map((m) => m.name).join(" & ")}
                      </div>
                      <div className="text-[10px] text-slate-500">{answeredCount} responses</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </ScrollArea>
        </aside>

        {/* Main Presentation Area */}
        <main className="flex-1 flex flex-col overflow-hidden">
          {/* Pair Info */}
          <div className="bg-slate-800/50 px-8 py-4 border-b border-slate-700/50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex -space-x-2">
                {currentPair?.members?.map((m) => (
                  <div key={m.user_id} className="w-9 h-9 rounded-full bg-indigo-500 flex items-center justify-center text-sm font-bold text-white ring-2 ring-slate-900">
                    {(m.name || "?")[0].toUpperCase()}
                  </div>
                ))}
              </div>
              <div>
                <div className="text-white font-heading font-semibold text-sm">
                  {currentPair?.members?.map((m) => m.name).join(" & ")}
                </div>
                <div className="text-slate-400 text-xs">
                  Question {currentQuestionIdx + 1} of {pairQuestions.length}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {pairQuestions.map((_, qi) => (
                <button
                  key={qi}
                  onClick={() => setCurrentQuestionIdx(qi)}
                  className={`w-2.5 h-2.5 rounded-full transition-colors ${
                    qi === currentQuestionIdx ? "bg-indigo-400" :
                    qi < currentQuestionIdx ? "bg-indigo-600" : "bg-slate-600"
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Question + Response */}
          <div className="flex-1 overflow-y-auto px-8 py-8 flex flex-col items-center justify-center">
            {currentQuestion ? (
              <div className="max-w-3xl w-full animate-fade-in" key={`${selectedPairIdx}-${currentQuestionIdx}`}>
                {/* Category */}
                <div className="flex items-center gap-2 mb-4">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${personaBg}`}>
                    <PersonaIcon className={`w-4 h-4 ${personaColor}`} />
                  </div>
                  <Badge className="bg-slate-700 text-slate-300 border-slate-600 text-xs">
                    {currentQuestion.category}
                  </Badge>
                </div>

                {/* Question */}
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-white leading-snug mb-8">
                  {currentQuestion.text}
                </h2>

                {/* Responses */}
                {questionResponses.length > 0 ? (
                  <div className="space-y-4">
                    {questionResponses.map((resp) => (
                      <div key={resp.user_id} className="bg-slate-800 rounded-2xl border border-slate-700 p-6">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-7 h-7 rounded-full bg-indigo-500 flex items-center justify-center text-[10px] font-bold text-white">
                            {(resp.user_name || "?")[0].toUpperCase()}
                          </div>
                          <span className="text-sm font-medium text-slate-300">{resp.user_name}</span>
                        </div>
                        <p className="text-base text-slate-200 leading-relaxed whitespace-pre-wrap">
                          {resp.response_text}
                        </p>
                        {resp.discussion_notes && (
                          <div className="mt-4 pt-3 border-t border-slate-700">
                            <div className="flex items-center gap-1.5 mb-1">
                              <MessageSquare className="w-3 h-3 text-slate-500" />
                              <span className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Discussion Notes</span>
                            </div>
                            <p className="text-sm text-slate-400 italic">{resp.discussion_notes}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-slate-800/50 rounded-2xl border border-dashed border-slate-700 p-8 text-center">
                    <p className="text-slate-500 text-sm">No response submitted for this question yet.</p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-slate-500">No questions available</p>
            )}
          </div>

          {/* Navigation Footer */}
          <div className="bg-slate-800 border-t border-slate-700 px-8 py-4 flex items-center justify-between shrink-0">
            <Button
              variant="ghost"
              data-testid="prev-question-btn"
              onClick={prevQuestion}
              disabled={currentQuestionIdx === 0}
              className="text-slate-400 hover:text-white disabled:opacity-30"
            >
              <ChevronLeft className="w-5 h-5 mr-1" />
              Previous
            </Button>

            <span className="text-xs text-slate-500 font-mono">
              {currentQuestionIdx + 1} / {pairQuestions.length}
            </span>

            <Button
              variant="ghost"
              data-testid="next-question-btn"
              onClick={nextQuestion}
              disabled={currentQuestionIdx === pairQuestions.length - 1}
              className="text-slate-400 hover:text-white disabled:opacity-30"
            >
              Next
              <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
          </div>
        </main>
      </div>
    </div>
  );
};

export default PresentationMode;
